import { ApplicationConfig, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { provideHttpClient, withInterceptors, withXhr, type HttpInterceptorFn } from '@angular/common/http';
import { provideUploadHttpInterceptors } from '@lite-workspace/uploader-core';
import { provideProductToasts } from '@lite-workspace/shared/helm-ui/toast';
import { provideSpartanHlm } from '@lite-workspace/shared/helm-ui/utils';
import { IDENTITY_DATA } from '@lite-workspace/shared/identity';
import { provideLumirEntryCookie } from '@lite-workspace/shared/services/analytics';
import {
  bearerInterceptor,
  errorInterceptor,
  initAuthMode,
  installSessionWatch,
  purgeLegacyClientTokens,
} from '@lite-workspace/shared/session';
import { provideVersionCheckToast, VERSION_RELOAD_BLOCKER } from '@lite-workspace/utils';
import { appRoutes } from './app.routes';
import { AnalyticsIdentityService } from './core/analytics-identity.service';
import { GalleryIdentityData } from './core/gallery-identity.data';
import { ThemeService } from './core/theme.service';
import { GALLERIES_DATA } from './features/galleries/domain/infrastructure/galleries.data';
import { HttpGalleriesData } from './features/galleries/domain/infrastructure/http-galleries.data';
import { GALLERY_MANAGE_DATA } from './features/gallery-manage/domain/infrastructure/gallery-manage.data';
import { HttpGalleryManageData } from './features/gallery-manage/domain/infrastructure/http-gallery-manage.data';
import { connectSupportWidget } from '@lite-workspace/shared/support-widget';
import { GALLERY_UPLOAD_DATA } from './features/gallery-manage/domain/infrastructure/gallery-upload.data';
import { HttpGalleryUploadData } from './features/gallery-manage/domain/infrastructure/http-gallery-upload.data';
import { UploadQueueStore } from './features/gallery-manage/domain/+state/upload-queue.store';

const APP_HTTP_INTERCEPTORS: HttpInterceptorFn[] = [bearerInterceptor, errorInterceptor];

export const appConfig: ApplicationConfig = {
  providers: [
    // Synchronous initializer: the entry is stored before the first navigation can redirect to sign-in and lose the referrer.
    provideLumirEntryCookie(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes, withComponentInputBinding(), withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    // XHR, not fetch: the browser's fetch cannot report how much of an upload has left the
    // machine, so on the fetch backend the upload bar could only ever count whole files.
    provideHttpClient(withXhr(), withInterceptors(APP_HTTP_INTERCEPTORS)),
    provideUploadHttpInterceptors(APP_HTTP_INTERCEPTORS),
    provideAppInitializer(() => void inject(ThemeService)),
    // Before the first guard run: the auth mode decides whether the cabinet proves itself with the
    // BFF cookie session or with the legacy token, and the whole session lib branches on it.
    provideAppInitializer(() => initAuthMode()),
    provideAppInitializer(() => void inject(AnalyticsIdentityService)),
    // Guests live in gallery-public: every page of this cabinet is the photographer's.
    provideAppInitializer(() => connectSupportWidget(() => 'default')),
    provideAppInitializer(() => purgeLegacyClientTokens()),
    // Выход или смена аккаунта в соседнем приложении браузера — вкладка узнаёт об этом на фокусе.
    provideAppInitializer(() => installSessionWatch()),
    provideSpartanHlm(),
    provideProductToasts(),
    provideVersionCheckToast(),
    // Перезагрузка посреди пачки теряет неотправленные файлы: пока идёт загрузка, новая версия ждёт в тосте.
    {
      provide: VERSION_RELOAD_BLOCKER,
      useFactory: () => {
        const uploads = inject(UploadQueueStore);
        return () => uploads.active();
      },
    },
    { provide: IDENTITY_DATA, useClass: GalleryIdentityData },
    { provide: GALLERIES_DATA, useClass: HttpGalleriesData },
    { provide: GALLERY_MANAGE_DATA, useClass: HttpGalleryManageData },
    { provide: GALLERY_UPLOAD_DATA, useClass: HttpGalleryUploadData },
  ],
};
